import { config } from '@iheartjane/config';
import type { FlexProps } from '@iheartjane/reefer';
import {
  Button,
  EditIcon,
  Flex,
  Image,
  Skeleton,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import { formatCurrency } from '@iheartjane/utils';
import capitalize from 'lodash-es/capitalize';
import isEqual from 'lodash-es/isEqual';
import { useMemo } from 'react';

import type {
  DeliveryGeofenceSchedule,
  DeliveryGeofenceV2,
  FulfillmentConfig,
} from '@jane/business-admin/types';
import {
  ALL_DAYS,
  BODY_LINE_HEIGHT,
  formatDuration,
} from '@jane/business-admin/util';

type EnabledDay =
  | 'enabled_monday'
  | 'enabled_tuesday'
  | 'enabled_wednesday'
  | 'enabled_thursday'
  | 'enabled_friday'
  | 'enabled_saturday'
  | 'enabled_sunday';

const LoadingTextBlock = () => (
  <Skeleton direction="column" animate>
    <Skeleton.Bone height={BODY_LINE_HEIGHT} mt={8} mb={8} mr={4} width="60%" />
    <Skeleton.Bone height={BODY_LINE_HEIGHT} mr={4} width="60%" />
  </Skeleton>
);
export const GeofenceLoadingItem = () => {
  return (
    <Flex mb={24}>
      <Skeleton animate direction="row">
        <Skeleton.Bone height={80} width={80} borderRadius="lg" mr={24} />
        <LoadingTextBlock />
        <LoadingTextBlock />
        <LoadingTextBlock />
        <LoadingTextBlock />
        <LoadingTextBlock />
        <Flex>
          <Button.Icon label="Edit" icon={<EditIcon />} disabled={true} />
          <Button.Icon label="Delete" icon={<TrashIcon />} disabled={true} />
        </Flex>
      </Skeleton>
    </Flex>
  );
};

const buildImageURL = (geojson: string) => {
  const settings = {
    username: 'mapbox',
    style_id: 'streets-v11',
    overlay: `geojson(${encodeURIComponent(geojson)})`,
    width: 80,
    height: 80,
    padding: 12,
  };
  return `https://api.mapbox.com/styles/v1/${settings.username}/${settings.style_id}/static/${settings.overlay}/auto/${settings.width}x${settings.height}?padding=${settings.padding}&access_token=${config.mapboxAccessToken}`;
};

const isPlural = (minutes: number) => {
  return minutes > 1 ? 's' : '';
};

const formatMinutes = (minutes: number | null | undefined) => {
  return minutes === null || minutes === undefined
    ? 'N/A'
    : `${minutes} minute${isPlural(minutes)}`;
};

export interface GeofenceSettingItemProps extends FlexProps {
  config?: FulfillmentConfig;
  geofence: DeliveryGeofenceV2;
  onDelete?: (geofenceId: number) => void;
  onEdit?: () => void;
  readonly?: boolean;
}

export const GeofenceSettingItem = ({
  geofence,
  readonly = true,
  onEdit,
  onDelete,
  config,
  ...flexProps
}: GeofenceSettingItemProps) => {
  const previewImage = useMemo(() => {
    return buildImageURL(geofence.geojson);
  }, [geofence.geojson]);

  const minMaxLeadTimeValue = (
    minutes: number | null,
    storeLevelMinutes: number | null | undefined,
    isMin: boolean
  ) => {
    return formatDuration(
      minutes && minutes > 0 ? minutes : storeLevelMinutes,
      (minutes === 0 || minutes === null) && storeLevelMinutes === 0
        ? isMin
          ? 'minimum'
          : 'maximum'
        : ''
    );
  };

  const daysSelectedFromSchedule = (
    geofenceSchedule: DeliveryGeofenceSchedule
  ) => {
    // want to take out the id from the keys in schedule
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [id, ...rest] = Object.keys(geofenceSchedule);
    const enabledDays = rest
      .filter((day) => geofenceSchedule[day as EnabledDay])
      .map((day) => capitalize(day.replace('enabled_', '')));

    return isEqual(
      enabledDays.sort(),
      ALL_DAYS.map((day) => capitalize(day)).sort()
    )
      ? 'Every Day'
      : enabledDays.join(', ');
  };

  const showWindowInterval = () => {
    if (!geofence.display_windows_by_day && !config?.display_windows_by_day) {
      return true;
    } else if (geofence.display_windows_by_day) {
      return false;
    } else if (
      !geofence.display_windows_by_day &&
      !geofence.window_minutes &&
      !geofence.interval_minutes &&
      config?.display_windows_by_day
    ) {
      return false;
    } else {
      return true;
    }
  };

  const schedulingValue = (
    minutes: number | null,
    storeLevelMinutes: number | undefined,
    isWindow: boolean
  ) => {
    return minutes === 0 && storeLevelMinutes === 0
      ? `No ${isWindow ? 'window' : 'interval'}`
      : `${formatMinutes(
          minutes && minutes > 0 ? minutes : storeLevelMinutes
        )} ${isWindow ? 'window' : 'interval'}`;
  };

  return (
    <Flex as="li" alignItems="center" {...flexProps}>
      <Flex width="120px" mr={24}>
        <Image
          width="120px"
          height="120px"
          altText="Preview of geofence"
          src={previewImage}
          borderRadius="rounded"
        />
      </Flex>
      <Flex flexDirection="column" width="100%">
        <Flex mb={24}>
          <Flex flexDirection="column" width="100%">
            <Typography color="grays-mid" mb={8}>
              Name
            </Typography>
            <Typography>{geofence.name || geofence.id}</Typography>
          </Flex>
          <Flex flexDirection="column" width="100%">
            <Typography color="grays-mid" mb={8}>
              Delivery fee
            </Typography>
            <Typography>{`${formatCurrency(geofence.fee)}`}</Typography>
          </Flex>
          <Flex flexDirection="column" width="100%">
            <Typography color="grays-mid" mb={8}>
              Minimum
            </Typography>
            <Typography>{`${formatCurrency(
              geofence.cart_minimum
            )}`}</Typography>
          </Flex>
          <Flex flexDirection="column" width="100%">
            <Typography color="grays-mid" mb={8}>
              Min lead time
            </Typography>
            <Typography data-testid="min-lead-time">
              {minMaxLeadTimeValue(
                geofence.min_lead_time_minutes,
                config?.min_lead_time_minutes,
                true
              )}
            </Typography>
          </Flex>
        </Flex>
        <Flex>
          <Flex flexDirection="column" width="25%">
            <Typography color="grays-mid" mb={8}>
              Max lead time
            </Typography>
            <Typography data-testid="max-lead-time">
              {minMaxLeadTimeValue(
                geofence.max_lead_time_minutes,
                config?.max_lead_time_minutes,
                false
              )}
            </Typography>
          </Flex>
          <Flex flexDirection="column" width={'25%'}>
            <Typography color="grays-mid" mb={8}>
              Last call
            </Typography>
            <Typography>
              {formatMinutes(geofence.last_call_minutes || 0)}
            </Typography>
          </Flex>
          <Flex flexDirection="column" width="25%">
            <Typography color="grays-mid" mb={8}>
              Scheduling
            </Typography>
            {showWindowInterval() ? (
              <>
                <Typography data-testid="window-time">
                  {schedulingValue(
                    geofence.window_minutes,
                    config?.window_minutes,
                    true
                  )}
                </Typography>
                <Typography data-testid="interval-time">
                  {schedulingValue(
                    geofence.interval_minutes,
                    config?.interval_minutes,
                    false
                  )}
                </Typography>
              </>
            ) : (
              <Typography data-testid="daily-window">Daily window</Typography>
            )}
          </Flex>
        </Flex>
        {geofence.delivery_geofence_schedule && (
          <Flex>
            <Flex flexDirection="column" width="25%">
              <Typography color="grays-mid" mb={8}>
                Days selected
              </Typography>
              <span>
                {daysSelectedFromSchedule(geofence.delivery_geofence_schedule)}
              </span>
            </Flex>
          </Flex>
        )}
      </Flex>
      {!readonly && (
        <Flex>
          <Button.Icon label="Edit" onClick={onEdit} icon={<EditIcon />} />
          <Button.Icon
            label="Delete"
            onClick={() => onDelete && onDelete(geofence.id)}
            icon={<TrashIcon />}
          />
        </Flex>
      )}
    </Flex>
  );
};
